import { forwardRef, memo, useCallback, useId } from 'react';
import { t } from '@lingui/macro';
import styled, { css } from 'styled-components';

import {
  EnableSchemaContextProvider,
  MicrodataHTMLAttributes,
  schema,
} from '@rover/components/src/microdata';
import { VerticalLayout } from '@rover/react-lib/src/components/layouts';
import useDeviceContext from '@rover/react-lib/src/context/useDeviceContext';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import { SearchResultsScrollDepthEvent } from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import {
  shouldShowCardRank,
  shouldShowMaximalSearchCard,
  shouldShowSimplifiedSearchCard,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import appendUrlParams from '@rover/rsdk/src/modules/Network/appendUrlParams';
import { snakeify } from '@rover/rsdk/src/modules/Network/object-utilities';
import { getSourceDescription } from '@rover/rsdk/src/modules/Network/userAgent';
import { useSearchUIContext } from '@rover/shared/js/providers/SearchUIContextProvider';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import type { RoverTheme } from '@rover/types/src/RoverTheme';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';
import emitMetric from '@rover/utilities/emitMetric';

import LegacyCardContent from './LegacyCardContent';
import MaximalCardContent from './MaximalCardContent';
import { SEARCH_RESULT_CARD_Z_INDEX } from './SearchResultCard.constants';
import SimplifiedCardContent from './SimplifiedCardContent';
import { searchResultToCommonSearchResult } from './utils';

export type Props = {
  displayExpanded: boolean;
  index?: number;
  hideFavorite?: boolean;
  isAuthenticated: boolean | null | undefined;
  language: string;
  memberProfileLinkParams: Record<string, string | undefined>;
  onBlur?: () => void;
  onFavoriteToggled: (personOpk: string) => void;
  onFocus?: (index: number) => void;
  onMouseEnter?: (index: number) => void;
  onMouseLeave?: () => void;
  searchResult: SearchResult;
  isSeoPage?: boolean;
  preferCatReviews: boolean;
  isAlternateResult?: boolean;
  onClick?: (
    entityOpk: string,
    rank: number | null,
    isBestMatch?: boolean,
    maybeBestMatch?: boolean
  ) => void;
  priceTransparencyLevel?: PriceTransparencyLevel;
  fromAlternateSearchResults?: boolean;
  isSelectedProviderResult?: boolean;
  itemProp?: MicrodataHTMLAttributes['itemProp'];
  showFromText?: boolean;
  isShowingPriceInCurrency?: boolean;
  isBHUser?: boolean;
  cardTreatment: SearchCardTreatment;
};

// Any elements that explicity need to be interactive in the search card
// should have z-index: 2. Otherwise, we want to be able to click through
// to the member profile, which is why this link sits on top of the entire card.
export const MemberProfileAnchor = styled.a`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: ${SEARCH_RESULT_CARD_Z_INDEX.PROFILE_ANCHOR.toNumber()};
  cursor: pointer;
`;

export const leftBoxShadow = (theme: RoverTheme): ReturnType<typeof css> =>
  css`
    box-shadow: inset ${theme.space['1x']} 0 0 0 ${theme.colors.interactive_text.primary_active};
  `;

export const maximalActiveShadow = (theme: RoverTheme): ReturnType<typeof css> =>
  css`
    box-shadow: inset 0 ${theme.space['1x']} 0 0 ${theme.colors.interactive_text.primary_active},
      ${theme.shadows.elevation_mid};
  `;

const MaximalSearchResultCardWrapper = styled(VerticalLayout)<{
  $isActive?: boolean;
  $isSelectedProviderResult?: boolean;
}>`
  position: relative;
  width: auto;
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: ${({ theme }) => theme.radii.primary};
  box-shadow: ${({ theme }) => theme.shadows.elevation_mid};
  margin-bottom: ${({ $isSelectedProviderResult, theme }) =>
    $isSelectedProviderResult ? theme.space['10x'] : '0'};
  margin: ${({ theme }) => theme.space['2x']};
  &:hover {
    ${({ theme }) => maximalActiveShadow(theme)};
  }
  ${({ $isActive, theme }) => ($isActive ? maximalActiveShadow(theme) : '')}
`;

export const SearchResultCardWrapper = styled(VerticalLayout)<{
  $isActive?: boolean;
  $isSelectedProviderResult?: boolean;
}>`
  &:hover {
    ${({ theme }) => leftBoxShadow(theme)};
  }

  ${({ $isActive, theme }) => ($isActive ? leftBoxShadow(theme) : '')}
  background-color: ${({ theme }) => theme.background.primary};
  padding: ${({ theme }) => theme.space['4x']};
  position: relative;
  width: 100%;
  margin-bottom: ${({ $isSelectedProviderResult, theme }) =>
    $isSelectedProviderResult ? theme.space['10x'] : '0'};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  }

  &:first-child {
    border-radius: ${({ theme }) => theme.radii.secondary} 0 0 0;
  }

  &:last-child {
    border-radius: 0 0 0 ${({ theme }) => theme.radii.secondary};
  }
`;

const SearchResultCard = forwardRef(function SearchResultCard(
  {
    displayExpanded,
    hideFavorite,
    index,
    isAuthenticated,
    language,
    memberProfileLinkParams,
    onBlur = () => {},
    onFavoriteToggled,
    onFocus = () => {},
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    searchResult,
    isSeoPage,
    preferCatReviews,
    onClick = () => {},
    priceTransparencyLevel,
    fromAlternateSearchResults,
    isSelectedProviderResult,
    itemProp,
    isAlternateResult = false,
    showFromText = true,
    isShowingPriceInCurrency = true,
    isBHUser = false,
    cardTreatment,
  }: Props,
  ref
) {
  const { personOpk, shortName, webUrl, rank, isBestMatch, maybeBestMatch } = searchResult;
  const { isDesktop } = useDeviceContext();
  const { hoveredResultIndex, setHoveredResultIndex } = useSearchUIContext();
  const indexExists = typeof index === 'number';

  const source = getSourceDescription();

  const handleScrollMetrics = useCallback(() => {
    const tags = {
      cardTreatment,
      source,
      rank: rank?.toString() ?? 'none',
    };
    emitAnalyticsEvent(new SearchResultsScrollDepthEvent(tags));
    if (typeof rank === 'number') {
      emitMetric(
        'search-scroll-depth',
        { cardTreatment, source },
        { metricType: 'distribution', metricValue: rank }
      );
    }
  }, [cardTreatment, rank, source]);

  const sectionRef = useOnIntersect(handleScrollMetrics, true, { threshold: 0.75 });

  const bhMemberProfileLinkParams = {
    ...memberProfileLinkParams,
    ...(isBHUser ? { isShowingPriceInCurrency: String(isShowingPriceInCurrency) } : {}),
  };
  const memberProfileLink =
    Object.keys(memberProfileLinkParams).length === 0
      ? webUrl
      : appendUrlParams(webUrl, snakeify(bhMemberProfileLinkParams));

  const showRank = shouldShowCardRank(cardTreatment, fromAlternateSearchResults, isSeoPage);
  const shouldShowPriceInCredits = !isShowingPriceInCurrency;
  const enableSchema = !isSeoPage;

  const handleBlur = (): void => {
    onBlur?.();
    setHoveredResultIndex(null);
  };

  const handleFocus = (): void => {
    if (indexExists) {
      onFocus?.(index);
    }
    setHoveredResultIndex(index ?? null);
  };

  const handleMouseEnter = (): void => {
    if (indexExists) {
      onMouseEnter?.(index);
    }
    setHoveredResultIndex(index ?? null);
  };

  const handleMouseLeave = (): void => {
    onMouseLeave?.();
  };

  const isMaximal = shouldShowMaximalSearchCard(cardTreatment, !!isSeoPage);
  const Wrapper = isMaximal ? MaximalSearchResultCardWrapper : SearchResultCardWrapper;
  const headingId = useId();
  const cardLabelId = isMaximal ? `sitter-name-${headingId}` : undefined;
  const rankedName = rank && showRank ? t`Search result number ${rank}: ${shortName}` : shortName;

  const handleClick = (): void => {
    onClick(personOpk, rank, !!isBestMatch, !!maybeBestMatch);
  };

  return (
    <EnableSchemaContextProvider enableSchema={enableSchema}>
      <Wrapper
        data-qa-id="search-results"
        data-testid="search-result-card"
        data-is-alternate={isAlternateResult}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        $isActive={hoveredResultIndex === index}
        // @ts-expect-error the default typing for a ref doesn't allow a second argument
        ref={(searchCard) => ref && ref(searchCard, index)}
        {...(enableSchema
          ? {
              itemScope: true,
              itemProp,
              itemType: schema`Person`,
            }
          : {})}
        $isSelectedProviderResult={isSelectedProviderResult}
        {...(isMaximal ? { role: 'article', 'aria-labelledby': cardLabelId } : {})}
      >
        {enableSchema && <meta itemProp="url" content={memberProfileLink} />}
        <MemberProfileAnchor
          onClick={handleClick}
          aria-label={isMaximal ? undefined : rankedName}
          href={memberProfileLink}
          rel={!isSeoPage ? undefined : 'nofollow'}
          target={isDesktop && !isSeoPage ? '_blank' : '_self'}
          {...(isMaximal ? { tabIndex: -1, 'aria-hidden': true } : {})}
        />
        {shouldShowMaximalSearchCard(cardTreatment, !!isSeoPage) && (
          <MaximalCardContent
            sectionRef={sectionRef}
            index={index}
            cardTreatment={cardTreatment}
            searchResult={searchResultToCommonSearchResult(searchResult)}
            preferCatReviews={preferCatReviews}
            language={language}
            showFromText={showFromText}
            isBHUser={isBHUser}
            shouldShowPriceInCredits={shouldShowPriceInCredits}
            priceTransparencyLevel={priceTransparencyLevel}
            profileUrl={memberProfileLink}
            analyticsProps={{
              cardTreatment,
              source,
              rank: rank?.toString() ?? 'none',
              personOpk,
            }}
            headingId={cardLabelId}
            onProfileClick={handleClick}
            isDesktop={isDesktop}
            isSeoPage={!!isSeoPage}
          />
        )}
        {shouldShowSimplifiedSearchCard(cardTreatment) && (
          <SimplifiedCardContent
            searchResult={searchResultToCommonSearchResult(searchResult)}
            index={index}
            language={language}
            showFromText={showFromText}
            isBHUser={isBHUser}
            shouldShowPriceInCredits={shouldShowPriceInCredits}
            priceTransparencyLevel={priceTransparencyLevel}
            cardTreatment={cardTreatment}
            sectionRef={sectionRef}
            preferCatReviews={preferCatReviews}
          />
        )}
        {!shouldShowMaximalSearchCard(cardTreatment, !!isSeoPage) &&
          !shouldShowSimplifiedSearchCard(cardTreatment) && (
            <LegacyCardContent
              searchResult={searchResult}
              displayExpanded={displayExpanded}
              index={index}
              isSeoPage={!!isSeoPage}
              showRank={showRank}
              language={language}
              showFromText={showFromText}
              isBHUser={isBHUser}
              shouldShowPriceInCredits={shouldShowPriceInCredits}
              isAuthenticated={isAuthenticated}
              onFavoriteToggled={() => onFavoriteToggled(personOpk)}
              hideFavorite={hideFavorite || isSeoPage}
              preferCatReviews={preferCatReviews}
              priceTransparencyLevel={priceTransparencyLevel}
              sectionRef={sectionRef}
            />
          )}
      </Wrapper>
    </EnableSchemaContextProvider>
  );
});

export default memo(SearchResultCard);
